import {
  type APIUser,
  bannerUrl,
  guildIconUrl,
  memberAvatarUrl,
  roleColorHex,
  sortedRoles,
} from '@alternativamais/chat-core';
import { useEffect, useState } from 'react';

import { Avatar } from '../../../componentes/Avatar';
import { Markdown } from '../../../componentes/Markdown';
import { avisar, avisarErro } from '../../../componentes/Avisos';
import { EmojiVisual } from '../SeletorEmoji';
import { useCliente, useDerivado, useEstado } from '../../../chat/contexto';
import { ARQUIVOS } from '../../../config';
import { corDoAvatar, iniciais } from '@alternativamais/chat-core';
import { noTamanho } from '../../../componentes/imagens';

/** Status personalizado de alguém (atividade tipo 4 da presença; o meu, das preferências). */
export function useStatusPersonalizado(userId: string): {
  texto: string | null;
  emoji: { id: string | null; name: string | null } | null;
} | null {
  return useDerivado(
    (estado) => {
      if (userId === estado.me?.id) {
        const meu = estado.userSettings?.custom_status;
        if (!meu || (!meu.text && !meu.emoji_name)) return null;
        if (meu.expires_at && Date.parse(meu.expires_at) <= Date.now())
          return null;
        return {
          texto: meu.text ?? null,
          emoji: meu.emoji_name
            ? { id: meu.emoji_id ?? null, name: meu.emoji_name }
            : null,
        };
      }
      const atividade = (estado.presences[userId]?.activities ?? []).find(
        (item) => (item as { type?: number }).type === 4,
      ) as
        | {
            state?: string | null;
            emoji?: { id: string | null; name: string | null } | null;
          }
        | undefined;
      if (!atividade) return null;
      return { texto: atividade.state ?? null, emoji: atividade.emoji ?? null };
    },
    (estado) => [
      userId,
      estado.me?.id,
      estado.userSettings,
      estado.presences[userId],
    ],
  );
}

export function TextoDoStatus({
  userId,
  className = '',
}: {
  userId: string;
  className?: string;
}) {
  const status = useStatusPersonalizado(userId);
  if (!status) return null;
  return (
    <span className={`flex min-w-0 items-center gap-1 ${className}`}>
      {status.emoji && <EmojiVisual emoji={status.emoji} tamanho={14} />}
      {status.texto && <span className="truncate">{status.texto}</span>}
    </span>
  );
}

const DATA_LONGA = new Intl.DateTimeFormat('pt-BR', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/**
 * O miolo do perfil (C12, e o painel da DM, D2): faixa com a cor da pessoa,
 * avatar, nomes, status, desde quando é membro, cargos e a nota.
 */
export function CorpoDoPerfil({
  usuario,
  guildId,
  fundo,
  rodape,
  bio,
  previa,
}: {
  usuario: APIUser;
  guildId: string | null;
  /** Cor de onde o perfil está (borda do avatar e da bolinha). */
  fundo: string;
  rodape?: React.ReactNode;
  /** "Sobre mim" (vem do `GET /users/:id/profile`). */
  bio?: string | null;
  /** Prévia nas configurações: avatar e faixa ainda não salvos, sem a nota. */
  previa?: {
    avatar?: string | null;
    faixa?: string | null;
    apelido?: string | null;
  };
}) {
  const membro = useEstado((estado) =>
    guildId ? estado.members[guildId]?.[usuario.id] : undefined,
  );
  const comum = useEstado((estado) =>
    guildId
      ? guildId
      : (estado.guildIds.find((id) => !!estado.members[id]?.[usuario.id]) ??
        null),
  );
  const servidor = useEstado((estado) =>
    comum ? estado.guilds[comum] : undefined,
  );
  const membroComum = useEstado((estado) =>
    comum ? estado.members[comum]?.[usuario.id] : undefined,
  );
  const cargos = useDerivado(
    (estado) => {
      const alvo = membro ?? membroComum;
      if (!alvo || !comum) return [];
      const meus = new Set(alvo.roles);
      return sortedRoles(estado, comum).filter((cargo) => meus.has(cargo.id));
    },
    (estado) => [comum, membro, membroComum, estado.guilds[comum ?? '']],
  );
  const status = useEstado(
    (estado) => estado.presences[usuario.id]?.status ?? 'offline',
  );
  const nome =
    (previa?.apelido !== undefined ? previa.apelido : membro?.nick) ||
    usuario.global_name ||
    usuario.username;
  const cor =
    usuario.accent_color != null
      ? roleColorHex(usuario.accent_color)
      : corDoAvatar(usuario.id);
  const faixa =
    previa?.faixa !== undefined ? previa.faixa : bannerUrl(ARQUIVOS, usuario);
  const avatarDoServidor =
    previa?.avatar !== undefined
      ? previa.avatar
      : guildId && membro?.avatar
        ? memberAvatarUrl(ARQUIVOS, guildId, membro, usuario.id)
        : undefined;
  return (
    <div className="flex flex-col">
      {faixa ? (
        <img
          src={noTamanho(faixa, 360)}
          alt=""
          className="h-[100px] w-full shrink-0 object-cover"
        />
      ) : (
        <div className="h-[60px] shrink-0" style={{ background: cor }} />
      )}
      <div className="relative px-4">
        <span
          className="absolute -top-10 rounded-full"
          style={{ boxShadow: `0 0 0 6px ${fundo}` }}
        >
          <Avatar
            pessoa={usuario}
            nome={nome}
            tamanho={80}
            status={status}
            fundo={fundo}
            src={avatarDoServidor}
          />
        </span>
      </div>
      <div className="flex flex-col gap-3 px-4 pt-12 pb-4">
        <div>
          <div className="flex items-center gap-1.5">
            <h3 className="m-0 truncate font-titulo text-xl font-bold">
              {nome}
            </h3>
            {usuario.bot && (
              <span className="rounded bg-destaque-suave px-[5px] py-px text-[10px] font-bold text-destaque-claro">
                BOT
              </span>
            )}
          </div>
          <div className="text-sm text-texto-2">{usuario.username}</div>
          <TextoDoStatus
            userId={usuario.id}
            className="mt-1.5 text-sm text-texto"
          />
        </div>
        <div className="flex flex-col gap-3 rounded-lg bg-[rgba(0,0,0,.18)] p-3">
          {bio && (
            <Secao titulo="Sobre mim">
              <div className="text-sm leading-normal whitespace-pre-wrap">
                <Markdown texto={bio} guildId={guildId} />
              </div>
            </Secao>
          )}
          {servidor && (membro ?? membroComum)?.joined_at && (
            <Secao titulo="Membro desde">
              <span className="flex items-center gap-2 text-sm">
                <IconeServidor guild={servidor} />
                {servidor.name} ·{' '}
                {DATA_LONGA.format(
                  new Date((membro ?? membroComum)!.joined_at!),
                )}
              </span>
            </Secao>
          )}
          {cargos.length > 0 && (
            <Secao titulo="Cargos">
              <span className="flex flex-wrap gap-1">
                {cargos.map((cargo) => (
                  <span
                    key={cargo.id}
                    className="flex h-6 items-center gap-1.5 rounded-md bg-ativo px-2 text-xs font-medium"
                  >
                    <span
                      className="size-2.5 rounded-full"
                      style={{
                        background: cargo.color
                          ? roleColorHex(cargo.color)
                          : 'var(--cor-texto-3)',
                      }}
                    />
                    {cargo.name}
                  </span>
                ))}
              </span>
            </Secao>
          )}
          {!previa && <Nota userId={usuario.id} />}
        </div>
        {rodape}
      </div>
    </div>
  );
}

function IconeServidor({
  guild,
}: {
  guild: { id: string; name: string; icon: string | null };
}) {
  const url = guildIconUrl(ARQUIVOS, guild);
  return url ? (
    <img src={url} alt="" className="size-5 rounded-md object-cover" />
  ) : (
    <span className="flex size-5 items-center justify-center rounded-md bg-destaque text-[9px] font-bold text-destaque-texto">
      {iniciais(guild.name).slice(0, 2)}
    </span>
  );
}

function Secao({
  titulo,
  children,
}: {
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-1.5">
      <h4 className="m-0 text-xs font-bold tracking-[.04em] text-texto-2 uppercase">
        {titulo}
      </h4>
      {children}
    </section>
  );
}

/** Nota no perfil (só você vê): grava ao sair do campo. */
function Nota({ userId }: { userId: string }) {
  const cliente = useCliente();
  const notas = useEstado((estado) => estado.notes);
  const salva = notas?.[userId] ?? '';
  const [texto, setTexto] = useState<string | null>(null);
  useEffect(() => {
    void cliente.loadNotes().catch(() => undefined);
  }, [cliente]);
  const valor = texto ?? salva;
  return (
    <Secao titulo="Nota">
      <label className="flex">
        <span className="sr-only">Nota sobre a pessoa</span>
        <textarea
          rows={2}
          maxLength={256}
          value={valor}
          disabled={notas === null}
          placeholder="Clique para adicionar uma nota"
          onChange={(evento) => setTexto(evento.target.value)}
          onBlur={() => {
            if (texto === null || texto.trim() === salva) return;
            cliente
              .setNote(userId, texto)
              .then(() => setTexto(null))
              .catch(avisarErro);
          }}
          className="w-full resize-none rounded-md border border-transparent bg-transparent p-1 text-sm text-texto outline-none placeholder:text-texto-3 focus:border-borda focus:bg-chat"
        />
      </label>
    </Secao>
  );
}

export function avisarCopiado(texto: string, aviso: string) {
  void navigator.clipboard
    ?.writeText(texto)
    .then(() => avisar(aviso, 'info'))
    .catch(() => avisar('Não deu para copiar.'));
}
